import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  business_name: string;
  is_active: boolean;
  created_at: string;
  last_login: string;
  wallet_balance: number;
  account_balance: number;
  total_payouts: number;
  total_transactions: number;
  role_name: string;
  client_id?: string;
  api_key?: string;
  client_secret?: string;
  merchants_created?: number;
}

interface CreateUserForm {
  name: string;
  email: string;
  password: string;
  phone: string;
  business_name: string;
  merchant_id: string;
  api_key: string;
  api_secret: string;
  role_id: number;
  selected_manager_id: string;
}

const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'managers' | 'merchants'>('managers');
  const [managers, setManagers] = useState<User[]>([]);
  const [merchants, setMerchants] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createUserType, setCreateUserType] = useState<'manager' | 'merchant'>('manager');
  
  // Pagination
  const [managersPage, setManagersPage] = useState(1);
  const [merchantsPage, setMerchantsPage] = useState(1);
  const [managersTotalPages, setManagersTotalPages] = useState(1);
  const [merchantsTotalPages, setMerchantsTotalPages] = useState(1);
  const [managersTotalCount, setManagersTotalCount] = useState(0);
  const [merchantsTotalCount, setMerchantsTotalCount] = useState(0);
  const [adminsTotalCount, setAdminsTotalCount] = useState(0);
  const limit = 10;

  // Search
  const [managersSearch, setManagersSearch] = useState('');
  const [merchantsSearch, setMerchantsSearch] = useState('');

  // Create form
  const [formData, setFormData] = useState<CreateUserForm>({
    name: '',
    email: '',
    password: '',
    phone: '',
    business_name: '',
    merchant_id: '',
    api_key: '',
    api_secret: '',
    role_id: 2, // manager
    selected_manager_id: ''
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Modal states for actions
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState<{ name: string; email: string; phone: string }>({
    name: '',
    email: '',
    phone: ''
  });
  const [editApiForm, setEditApiForm] = useState<{ client_id: string; api_key: string; client_secret: string }>({
    client_id: '',
    api_key: '',
    client_secret: ''
  });

  // Merchants-by-manager modal
  const [showMerchantsModal, setShowMerchantsModal] = useState(false);
  const [selectedManagerForMerchants, setSelectedManagerForMerchants] = useState<User | null>(null);
  const [managerMerchants, setManagerMerchants] = useState<User[]>([]);
  const [loadingManagerMerchants, setLoadingManagerMerchants] = useState(false);

  useEffect(() => {
    fetchManagers();
  }, [managersPage, managersSearch]);

  useEffect(() => {
    fetchMerchants();
  }, [merchantsPage, merchantsSearch]);

  // Fetch admin count once
  useEffect(() => {
    fetchAdminsCount();
  }, []);

  const fetchManagers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({
        page: managersPage,
        limit,
        role: 'manager',
        search: managersSearch,
        sync_wallet: true
      });
      
      setManagers(response.data.data.users);
      setManagersTotalPages(response.data.data.pagination.totalPages);
      setManagersTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({
        page: merchantsPage,
        limit,
        role: 'merchant',
        search: merchantsSearch
      });
      
      setMerchants(response.data.data.users);
      setMerchantsTotalPages(response.data.data.pagination.totalPages);
      setMerchantsTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminsCount = async () => {
    try {
      const response = await adminAPI.getAllUsers({
        page: 1,
        limit: 1,
        role: 'admin'
      });
      setAdminsTotalCount(response.data.data.pagination.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching admins count:', error);
      setAdminsTotalCount(0);
    }
  };

  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    const user = [...managers, ...merchants].find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowToggleModal(true);
    }
  };

  const confirmToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      await adminAPI.toggleUserStatus(selectedUser.id, !selectedUser.is_active);
      
      // Refresh the appropriate list
      if (activeTab === 'managers') {
        fetchManagers();
      } else {
        fetchMerchants();
      }
      
      setShowToggleModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleResetPassword = async (userId: number, userName: string) => {
    const user = [...managers, ...merchants].find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setNewPassword('');
      setShowPasswordModal(true);
    }
  };

  const confirmResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    if (newPassword.length < 6) {
      return;
    }

    try {
      await adminAPI.resetUserPassword(selectedUser.id, newPassword);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    const user = [...managers, ...merchants].find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await adminAPI.deleteUser(selectedUser.id);
      
      // Refresh the appropriate list
      if (activeTab === 'managers') {
        fetchManagers();
      } else {
        fetchMerchants();
      }
      
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleCreateUser = (type: 'manager' | 'merchant') => {
    setCreateUserType(type);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      business_name: '',
      merchant_id: '',
      api_key: '',
      api_secret: '',
      role_id: type === 'manager' ? 2 : 4, // 2 for manager, 4 for merchant
      selected_manager_id: ''
    });
    setFormErrors([]);
    setShowCreateModal(true);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.name.trim()) {
      errors.push('Name is required');
    }

    if (!formData.email.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Invalid email format');
    }

    if (!formData.password) {
      errors.push('Password is required');
    } else if (formData.password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }

    if (createUserType === 'manager') {
      if (!formData.merchant_id.trim()) {
        errors.push('Client ID is required for managers');
      }
      if (!formData.api_key.trim()) {
        errors.push('API Key is required for managers');
      }
      if (!formData.api_secret.trim()) {
        errors.push('Client Secret is required for managers');
      }
    }

    if (createUserType === 'merchant') {
      if (!formData.selected_manager_id.trim()) {
        errors.push('Please select a manager for this merchant');
      }
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await adminAPI.createUser(formData);
      alert(`${createUserType === 'manager' ? 'Manager' : 'Merchant'} created successfully!`);
      setShowCreateModal(false);
      
      // Refresh the appropriate list
      if (createUserType === 'manager') {
        fetchManagers();
      } else {
        fetchMerchants();
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      alert(error.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderUserTable = (users: User[], type: 'managers' | 'merchants') => {
    console.log(`Rendering ${type} table with ${users.length} users:`, users);
    
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {type}...</p>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <h3>No {type} found</h3>
          <p>Create your first {type === 'managers' ? 'manager' : 'merchant'} to get started</p>
          <button 
            className="btn-primary"
            onClick={() => handleCreateUser(type === 'managers' ? 'manager' : 'merchant')}
          >
            Create {type === 'managers' ? 'Manager' : 'Merchant'}
          </button>
        </div>
      );
    }

    return (
      <div className="table-container">
        <table className={`transaction-table ${type}-table`}>
          <thead>
            <tr>
              <th>ID</th>
              <th>{type === 'managers' ? 'MANAGER NAME' : 'MERCHANT NAME'}</th>
              <th>EMAIL</th>
              <th>WALLET BALANCE</th>
              {type === 'managers' && <th>ACCOUNT BALANCE</th>}
              {type === 'managers' && <th>MERCHANTS</th>}
              {type === 'merchants' && <th>PAYOUTS</th>}
              <th>STATUS</th>
              <th>CREATED</th>
              <th>LAST LOGIN</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                <td>{user.id}</td>
                <td>
                  <div className="user-info1">
                    <strong>{user.name}</strong>
                    <span className="user-email">{user.email}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td className="currency">{formatCurrency(user.wallet_balance)}</td>
                {type === 'managers' && <td className="currency">{formatCurrency(user.account_balance || 0)}</td>}
                {type === 'managers' && (
                  <td className="center">
                    <button
                      className="btn-link"
                      onClick={async () => {
                        setSelectedManagerForMerchants(user);
                        setShowMerchantsModal(true);
                        try {
                          setLoadingManagerMerchants(true);
                          const resp = await adminAPI.getAllUsers({ role: 'merchant', created_by: user.id, page: 1, limit: 100 });
                          setManagerMerchants(resp.data.data.users || []);
                        } catch (e) {
                          console.error('Error loading manager merchants', e);
                          setManagerMerchants([]);
                        } finally {
                          setLoadingManagerMerchants(false);
                        }
                      }}
                      title="View merchants created by this manager"
                    >
                      {user.merchants_created ?? 0}
                    </button>
                  </td>
                )}
                {type === 'merchants' && <td className="center">{user.total_payouts || 0}</td>}
                <td>
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? '✓ Active' : '✗ Inactive'}
                  </span>
                </td>
                <td className="date">{formatDate(user.created_at)}</td>
                <td className="date">{formatDate(user.last_login)}</td>
                <td className="actions-cell">
                  <div className="action-buttons">
                    <button
                      className="btn-icon btn-primary"
                      onClick={() => {
                        setSelectedUser(user);
                      setEditForm({
                        name: user.name || '',
                        email: user.email || '',
                        phone: user.phone || ''
                      });
                      setEditApiForm({
                        client_id: user.client_id || '',
                        api_key: user.api_key || '',
                        client_secret: user.client_secret || ''
                      });
                        setShowEditModal(true);
                      }}
                      title="Edit User"
                    >
                      ✏️
                    </button>
                    <button
                      className={`btn-icon ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleStatus(user.id, user.is_active)}
                      title={user.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {user.is_active ? '🔒' : '🔓'}
                    </button>
                    <button
                      className="btn-icon btn-info"
                      onClick={() => handleResetPassword(user.id, user.name)}
                      title="Reset Password"
                    >
                      🔑
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteUser(user.id, user.name)}
                      title="Delete User"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ← Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <div className="header-content">
          <h1>👥 User Management</h1>
          <p>Manage managers and merchants</p>
        </div>
        <div className="header-actions">
          {activeTab === 'managers' && (
            <button 
              className="btn-primary"
              onClick={() => handleCreateUser('manager')}
            >
              ➕ Create Manager
            </button>
          )}
          {activeTab === 'merchants' && (
            <button 
              className="btn-primary"
              onClick={() => handleCreateUser('merchant')}
            >
              ➕ Create Merchant
            </button>
          )}
        </div>
      </div>

      {/* Summary Counts */}
      <div className="summary-cards">
        <div className="summary-card credit">
          <div className="card-icon">👔</div>
          <div className="card-content">
            <h3>TOTAL MANAGERS</h3>
            <p className="card-value">{managersTotalCount}</p>
          </div>
        </div>
        <div className="summary-card credit">
          <div className="card-icon">🏪</div>
          <div className="card-content">
            <h3>TOTAL MERCHANTS</h3>
            <p className="card-value">{merchantsTotalCount}</p>
          </div>
        </div>
        <div className="summary-card credit">
          <div className="card-icon">👑</div>
          <div className="card-content">
            <h3>TOTAL ADMINS</h3>
            <p className="card-value">{adminsTotalCount}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'managers' ? 'active' : ''}`}
          onClick={() => setActiveTab('managers')}
        >
          <span className="tab-icon">👔</span>
          <span className="tab-label">Managers</span>
          <span className="tab-count">{managers.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          <span className="tab-icon">🏪</span>
          <span className="tab-label">Merchants</span>
          <span className="tab-count">{merchants.length}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder={`Search ${activeTab}...`}
          value={activeTab === 'managers' ? managersSearch : merchantsSearch}
          onChange={(e) => {
            if (activeTab === 'managers') {
              setManagersSearch(e.target.value);
              setManagersPage(1);
            } else {
              setMerchantsSearch(e.target.value);
              setMerchantsPage(1);
            }
          }}
        />
      </div>

      {/* User Tables */}
      {activeTab === 'managers' && (
        <>
          {renderUserTable(managers, 'managers')}
          {renderPagination(managersPage, managersTotalPages, setManagersPage)}
        </>
      )}

      {activeTab === 'merchants' && (
        <>
          {renderUserTable(merchants, 'merchants')}
          {renderPagination(merchantsPage, merchantsTotalPages, setMerchantsPage)}
        </>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create {createUserType === 'manager' ? 'Manager' : 'Merchant'}</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmitCreate}>
              <div className="modal-body1">
                {formErrors.length > 0 && (
                  <div className="error-messages">
                    {formErrors.map((error, index) => (
                      <div key={index} className="error-message">⚠️ {error}</div>
                    ))}
                  </div>
                )}

                {createUserType === 'merchant' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="selected_manager_id">Choose From Managers List *</label>
                      <select
                        id="selected_manager_id"
                        className="form-control"
                        value={formData.selected_manager_id}
                        onChange={(e) => setFormData({ ...formData, selected_manager_id: e.target.value })}
                      >
                        <option value="">Select a manager...</option>
                        {managers.map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name} ({manager.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    {formData.selected_manager_id && (() => {
                      const selectedManager = managers.find((m) => String(m.id) === String(formData.selected_manager_id));
                      return selectedManager ? (
                        <>
                          <div className="form-group">
                            <label>Client ID</label>
                            <input
                              type="text"
                              className="form-control"
                              value={selectedManager.client_id || '—'}
                              readOnly
                              style={{ backgroundColor: 'var(--input-readonly-bg, #f5f5f5)', cursor: 'default' }}
                            />
                            <small className="form-text text-muted">From selected manager (will be assigned to merchant)</small>
                          </div>
                          <div className="form-group">
                            <label>Client Secret</label>
                            <input
                              type="text"
                              className="form-control"
                              value={selectedManager.client_secret || '—'}
                              readOnly
                              style={{ backgroundColor: 'var(--input-readonly-bg, #f5f5f5)', cursor: 'default' }}
                            />
                            <small className="form-text text-muted">From selected manager (will be assigned to merchant)</small>
                          </div>
                          <div className="form-group">
                            <label>API Key</label>
                            <input
                              type="text"
                              className="form-control"
                              value={selectedManager.api_key || '—'}
                              readOnly
                              style={{ backgroundColor: 'var(--input-readonly-bg, #f5f5f5)', cursor: 'default' }}
                            />
                            <small className="form-text text-muted">From selected manager (will be assigned to merchant)</small>
                          </div>
                        </>
                      ) : null;
                    })()}
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    className="form-control"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter password (min 6 characters)"
                  />
                </div>

                {createUserType === 'manager' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="phone">Mobile Number</label>
                      <input
                        type="tel"
                        id="phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={15}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="merchant_id">Merchant ID *</label>
                      <input
                        type="text"
                        id="merchant_id"
                        className="form-control"
                        value={formData.merchant_id}
                        onChange={(e) => setFormData({ ...formData, merchant_id: e.target.value })}
                        placeholder="Enter Merchant ID"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="api_key">API Key *</label>
                      <input
                        type="text"
                        id="api_key"
                        className="form-control"
                        value={formData.api_key}
                        onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                        placeholder="Enter API Key"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="api_secret">API Secret *</label>
                      <input
                        type="text"
                        id="api_secret"
                        className="form-control"
                        value={formData.api_secret}
                        onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                        placeholder="Enter API Secret"
                      />
                    </div>
                  </>
                )}

              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : `Create ${createUserType === 'manager' ? 'Manager' : 'Merchant'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toggle Status Modal */}
      {showToggleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowToggleModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedUser.is_active ? 'Deactivate' : 'Activate'} User</h2>
              <button className="modal-close" onClick={() => setShowToggleModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="confirmation-content">
                <div className="confirmation-icon">
                  {selectedUser.is_active ? '🔒' : '🔓'}
                </div>
                <div className="confirmation-text">
                  <h3>Are you sure you want to {selectedUser.is_active ? 'deactivate' : 'activate'} this user?</h3>
                  <div className="user-info1">
                    <p><strong>Name:</strong> {selectedUser.name}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Current Status:</strong> 
                      <span className={`status-badge ${selectedUser.is_active ? 'active' : 'inactive'}`}>
                        {selectedUser.is_active ? '✓ Active' : '✗ Inactive'}
                      </span>
                    </p>
                  </div>
                  <p
                    className="confirmation-warning"
                    style={{
                      background: 'rgba(104, 96, 195, 0.95)', 
                      color: 'rgba(98, 61, 0, 0.95)',
                      border: '1px solid rgba(251, 191, 36, 0.55)',
                      borderRadius: 12,
                      padding: '12px 16px'
                    }}
                  >
                    {selectedUser.is_active 
                      ? 'The user will not be able to access their account after deactivation.' 
                      : 'The user will be able to access their account after activation.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowToggleModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn-primary ${selectedUser.is_active ? 'btn-warning' : 'btn-success'}`}
                onClick={confirmToggleStatus}
              >
                {selectedUser.is_active ? 'Deactivate User' : 'Activate User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="user-info1">
                <p><strong>Name:</strong> {selectedUser.name}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password *</label>
                <input
                  type="password"
                  id="newPassword"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                />
                {newPassword && newPassword.length < 6 && (
                  <div className="error-message">Password must be at least 6 characters long</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmResetPassword}
                disabled={!newPassword || newPassword.length < 6}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content1 danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deactivate User</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="confirmation-content">
                <div className="confirmation-icon">🗑️</div>
                <div className="confirmation-text">
                  <h3>Are you sure you want to deactivate this user?</h3>
                  <div className="user-info1">  
                    <p><strong>Name:</strong> {selectedUser.name}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Role:</strong> {selectedUser.role_name}</p>
                  </div>
                  <p
                    className="confirmation-warning"
                    style={{
                      background: 'rgba(82, 85, 177, 0.95)',
                      color: 'rgba(98, 61, 0, 0.95)',
                      border: '1px solid rgba(251, 191, 36, 0.55)',
                      borderRadius: 12,
                      padding: '12px 16px'
                    }}
                  >
                    This action will deactivate the user account. The user will not be able to access their account, but this action can be reversed later by reactivating the user.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDeleteUser}
              >
                Deactivate User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit {selectedUser.role_name === 'manager' ? 'Manager' : selectedUser.role_name === 'merchant' ? 'Merchant' : 'User'}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              <div className="form-group">
                <label htmlFor="edit_name">Full Name</label>
                <input
                  type="text"
                  id="edit_name"
                  className="form-control"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit_email">Email Address</label>
                <input
                  type="email"
                  id="edit_email"
                  className="form-control"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit_phone">Mobile Number</label>
                <input
                  type="tel"
                  id="edit_phone"
                  className="form-control"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={15}
                />
              </div>

              {/* API Fields - Show for managers */}
              {selectedUser.role_name === 'manager' && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit_client_id">Client ID</label>
                    <input
                      type="text"
                      id="edit_client_id"
                      className="form-control"
                      value={editApiForm.client_id}
                      onChange={(e) => setEditApiForm({ ...editApiForm, client_id: e.target.value })}
                      placeholder="Enter client ID"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_client_secret">Client Secret</label>
                    <input
                      type="text"
                      id="edit_client_secret"
                      className="form-control"
                      value={editApiForm.client_secret}
                      onChange={(e) => setEditApiForm({ ...editApiForm, client_secret: e.target.value })}
                      placeholder="Enter client secret"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit_api_key">API Key</label>
                    <input
                      type="text"
                      id="edit_api_key"
                      className="form-control"
                      value={editApiForm.api_key}
                      onChange={(e) => setEditApiForm({ ...editApiForm, api_key: e.target.value })}
                      placeholder="Enter API key"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={editSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  if (!selectedUser) return;
                  try {
                    setEditSubmitting(true);
                    const updateData: any = {
                      name: editForm.name,
                      email: editForm.email,
                      phone: editForm.phone || undefined
                    };
                    // Add API fields for managers
                    if (selectedUser.role_name === 'manager') {
                      updateData.client_id = editApiForm.client_id;
                      updateData.api_key = editApiForm.api_key;
                      updateData.client_secret = editApiForm.client_secret;
                    }
                    await adminAPI.updateUser(selectedUser.id, updateData);
                    setShowEditModal(false);
                    // refresh current tab
                    if (activeTab === 'managers') {
                      fetchManagers();
                    } else {
                      fetchMerchants();
                    }
                  } catch (e) {
                    console.error('Error updating user', e);
                    alert('Failed to update user');
                  } finally {
                    setEditSubmitting(false);
                  }
                }}
                disabled={editSubmitting}
              >
                {editSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merchants by Manager Modal */}
      {showMerchantsModal && (
        <div className="modal-overlay" onClick={() => setShowMerchantsModal(false)}>
          <div className="modal-content1 modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Merchants created by {selectedManagerForMerchants?.name}</h2>
              <button className="modal-close" onClick={() => setShowMerchantsModal(false)}>✕</button>
            </div>
            <div className="modal-body1">
              {loadingManagerMerchants ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading merchants...</p>
                </div>
              ) : managerMerchants.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📭</span>
                  <h3>No merchants found</h3>
                  <p>This manager hasn't created any merchants yet.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="transaction-table merchants-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>NAME</th>
                        <th>EMAIL</th>
                        <th>WALLET BALANCE</th>
                        <th>ACCOUNT BALANCE</th>
                        <th>PAYOUTS</th>
                        <th>STATUS</th>
                        <th>CREATED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {managerMerchants.map((m) => (
                        <tr key={m.id} className={!m.is_active ? 'inactive-row' : ''}>
                          <td>{m.id}</td>
                          <td>
                            <div className="user-info1">
                              <strong>{m.name}</strong>
                              <span className="user-email">{m.email}</span>
                            </div>
                          </td>
                          <td>{m.email}</td>
                          <td className="currency">{formatCurrency(m.wallet_balance)}</td>
                          <td className="currency">{formatCurrency(m.account_balance || 0)}</td>
                          <td className="center">{m.total_payouts || 0}</td>
                          <td>
                            <span className={`status-badge ${m.is_active ? 'active' : 'inactive'}`}>
                              {m.is_active ? '✓ Active' : '✗ Inactive'}
                            </span>
                          </td>
                          <td className="date">{formatDate(m.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowMerchantsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

